import React, { useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { downloadFile } from "@/lib/download";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash2, Loader2, Save, FileText, Download, Pencil,
  Undo2, Send, CheckCircle2, AlertTriangle,
} from "lucide-react";

const money = (n) => (Number(n) || 0).toLocaleString("en-AE", { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const VAT_OPTS = [{ v: "standard", l: "5%" }, { v: "zero", l: "0%" }, { v: "exempt", l: "Exempt" }];
const STATUS_STYLE = {
  RECEIVED: "bg-amber-100 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300",
  SENT: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  DELIVERED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  REPORTED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  APPROVED: "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300",
  REJECTED: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  NOT_COMPLIANT: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  FAILED: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  INVALID: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  ACKNOWLEDGED: "bg-blue-100 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
  ERROR: "bg-red-100 text-red-700 dark:bg-red-950/40 dark:text-red-300",
};
const DONE_OK = ["APPROVED", "REPORTED", "DELIVERED"];
const DONE_BAD = ["REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED"];

export default function CreditNotes() {
  const navigate = useNavigate();
  const [params, setParams] = useSearchParams();
  const [mode, setMode] = useState("list");
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [subs, setSubs] = useState({});
  const [subBusy, setSubBusy] = useState("");
  const [busy, setBusy] = useState(false);
  const [dl, setDl] = useState("");
  const [editing, setEditing] = useState(null); // {id?, source_kind, source_id, source_invoice_number, reason, issue_date, customer, lines}
  const prepared = React.useRef(false);

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([api.get("/einvoice/credit-notes"), api.get("/einvoice/submissions")])
      .then(([cn, s]) => {
        setList(cn.data.credit_notes || []);
        const m = {};
        (s.data.submissions || []).forEach((x) => { if (x.doc_kind === "credit_note") m[x.doc_id] = x; });
        setSubs(m);
      }).catch(() => {}).finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  // deep-link from an invoice / IPC: ?source_kind=&source_id=
  useEffect(() => {
    const sk = params.get("source_kind"); const sid = params.get("source_id");
    if (sk && sid && !prepared.current) {
      prepared.current = true;
      setParams({}, { replace: true });
      api.get(`/einvoice/credit-notes/prepare`, { params: { source_kind: sk, source_id: sid } })
        .then((r) => {
          setEditing({
            source_kind: sk, source_id: sid,
            source_invoice_number: r.data.source_invoice_number,
            reason: "", issue_date: new Date().toISOString().slice(0, 10),
            customer: r.data.customer || {}, lines: r.data.lines || [],
          });
          setMode("edit");
        })
        .catch((e) => toast.error(e.response?.data?.detail || "Could not prepare the credit note"));
    }
  }, [params, setParams]);

  const openEdit = async (cn) => {
    const r = await api.get(`/einvoice/credit-notes/${cn.id}`);
    const d = r.data;
    setEditing({ id: d.id, source_kind: d.source_kind, source_id: d.source_id,
      source_invoice_number: d.source_invoice_number, reason: d.reason, issue_date: d.issue_date,
      customer: d.customer || {}, lines: (d.lines || []).map((l) => ({ ...l })) });
    setMode("edit");
  };

  const setLine = (i, k, v) => setEditing((e) => ({ ...e, lines: e.lines.map((l, idx) => (idx === i ? { ...l, [k]: v } : l)) }));
  const addLine = () => setEditing((e) => ({ ...e, lines: [...e.lines, { description: "", qty: 1, unit: "unit", unit_price: 0, vat_rate: "standard" }] }));
  const delLine = (i) => setEditing((e) => ({ ...e, lines: e.lines.filter((_, idx) => idx !== i) }));

  const totals = (() => {
    let std = 0, nontax = 0;
    (editing?.lines || []).forEach((l) => {
      const net = (parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0);
      if (l.vat_rate === "standard") std += net; else nontax += net;
    });
    const vat = Math.round(std * 5) / 100;
    return { subtotal: std, nontax, vat, total: Math.round((std + nontax + vat) * 100) / 100 };
  })();

  const save = async () => {
    setBusy(true);
    try {
      const payload = {
        source_kind: editing.source_kind, source_id: editing.source_id,
        reason: editing.reason, issue_date: editing.issue_date,
        customer: editing.customer,
        lines: editing.lines.map((l) => ({ description: l.description, qty: parseFloat(l.qty) || 0,
          unit: l.unit || "unit", unit_price: parseFloat(l.unit_price) || 0, vat_rate: l.vat_rate || "standard" })),
      };
      if (editing.id) await api.put(`/einvoice/credit-notes/${editing.id}`, payload);
      else await api.post(`/einvoice/credit-notes`, payload);
      toast.success(editing.id ? "Credit note updated" : "Credit note created");
      setMode("list"); setEditing(null); load();
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save the credit note"); }
    finally { setBusy(false); }
  };

  const remove = async (cn) => {
    if (!window.confirm(`Delete credit note ${cn.cn_number}?`)) return;
    try { await api.delete(`/einvoice/credit-notes/${cn.id}`); toast.success("Credit note deleted"); load(); }
    catch { toast.error("Could not delete"); }
  };

  const download = async (cn, fmt) => {
    setDl(cn.id + fmt);
    try { await downloadFile(`/einvoice/credit-notes/${cn.id}/download?fmt=${fmt}`, `${cn.cn_number}.${fmt === "xlsx" ? "xlsx" : fmt}`); }
    catch { toast.error("Download failed"); }
    finally { setDl(""); }
  };

  const submitJam = async (cn) => {
    setSubBusy(cn.id);
    try {
      const r = await api.post(`/einvoice/credit-notes/${cn.id}/submit-wafeq`);
      setSubs((m) => ({ ...m, [cn.id]: r.data }));
      toast.success("Submitted to the FTA via Wafeq — clearance in progress");
      let tries = 0;
      const poll = async () => {
        tries += 1;
        try {
          const s = await api.get(`/einvoice/submissions/credit_note/${cn.id}`);
          if (s.data.submission) {
            setSubs((m) => ({ ...m, [cn.id]: s.data.submission }));
            if (["APPROVED", "REPORTED", "DELIVERED", "REJECTED", "INVALID", "NOT_COMPLIANT", "FAILED", "ACKNOWLEDGED", "ERROR"].includes(s.data.submission.status)) return;
          }
        } catch { /* ignore */ }
        if (tries < 8) setTimeout(poll, 4000);
      };
      setTimeout(poll, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not submit to the FTA");
      try { const s = await api.get(`/einvoice/submissions/credit_note/${cn.id}`); if (s.data.submission) setSubs((m) => ({ ...m, [cn.id]: s.data.submission })); } catch { /* ignore */ }
    } finally { setSubBusy(""); }
  };

  if (loading) return <div className="flex items-center gap-2 text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>;

  if (mode === "edit" && editing) {
    return (
      <div data-testid="cn-editor">
        <button onClick={() => { setMode("list"); setEditing(null); }} data-testid="cn-editor-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Credit notes</button>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Undo2 className="w-6 h-6 text-rose-500" /> {editing.id ? "Edit credit note" : "New credit note"}</h1>
        <p className="text-sm text-muted-foreground mt-1 mb-6">Against {editing.source_kind === "ipc" ? "IPC" : "invoice"} <strong>{editing.source_invoice_number}</strong> — corrects it compliantly instead of editing the issued document.</p>

        <div className="rounded-2xl border border-border bg-card p-5 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Customer</label>
              <Input data-testid="cn-customer-name" value={editing.customer.company_name || ""} onChange={(e) => setEditing({ ...editing, customer: { ...editing.customer, company_name: e.target.value } })} className="mt-1.5" placeholder="Customer name" />
            </div>
            <div>
              <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Issue date</label>
              <Input data-testid="cn-issue-date" type="date" value={editing.issue_date || ""} onChange={(e) => setEditing({ ...editing, issue_date: e.target.value })} className="mt-1.5" />
            </div>
          </div>
          <div>
            <label className="text-xs font-bold uppercase tracking-wider text-muted-foreground">Reason for credit</label>
            <Textarea data-testid="cn-reason" value={editing.reason || ""} onChange={(e) => setEditing({ ...editing, reason: e.target.value })} rows={2} className="mt-1.5" placeholder="e.g. Overcharged quantity on original invoice / returned goods" />
          </div>

          <div className="overflow-x-auto border border-border rounded-xl">
            <table className="w-full text-sm">
              <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="px-3 py-2 text-left font-bold">Description</th>
                  <th className="px-3 py-2 text-right font-bold w-20">Qty</th>
                  <th className="px-3 py-2 text-right font-bold w-28">Unit price</th>
                  <th className="px-3 py-2 text-left font-bold w-24">VAT</th>
                  <th className="px-3 py-2 text-right font-bold w-28">Amount</th>
                  <th className="w-10"></th>
                </tr>
              </thead>
              <tbody>
                {editing.lines.map((l, i) => (
                  <tr key={i} className="border-t border-border">
                    <td className="px-2 py-1"><Input className="h-8" data-testid={`cn-line-desc-${i}`} value={l.description} onChange={(e) => setLine(i, "description", e.target.value)} /></td>
                    <td className="px-2 py-1"><Input type="number" className="h-8 text-right" data-testid={`cn-line-qty-${i}`} value={l.qty} onChange={(e) => setLine(i, "qty", e.target.value)} /></td>
                    <td className="px-2 py-1"><Input type="number" className="h-8 text-right" data-testid={`cn-line-price-${i}`} value={l.unit_price} onChange={(e) => setLine(i, "unit_price", e.target.value)} /></td>
                    <td className="px-2 py-1">
                      <select data-testid={`cn-line-vat-${i}`} value={l.vat_rate || "standard"} onChange={(e) => setLine(i, "vat_rate", e.target.value)} className="h-8 w-full rounded-md border border-input bg-background px-2 text-sm">
                        {VAT_OPTS.map((o) => <option key={o.v} value={o.v}>{o.l}</option>)}
                      </select>
                    </td>
                    <td className="px-2 py-1 text-right font-semibold tabular-nums">{money((parseFloat(l.qty) || 0) * (parseFloat(l.unit_price) || 0))}</td>
                    <td className="px-2 py-1 text-center"><button onClick={() => delLine(i)} className="text-muted-foreground hover:text-destructive"><Trash2 className="w-4 h-4" /></button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-between">
            <Button variant="ghost" size="sm" className="gap-1.5" data-testid="cn-add-line" onClick={addLine}><Plus className="w-4 h-4" /> Add line</Button>
            <div className="text-right text-sm">
              <div className="text-muted-foreground">Taxable AED {money(totals.subtotal)} · VAT AED {money(totals.vat)}{totals.nontax ? ` · Non-taxable AED ${money(totals.nontax)}` : ""}</div>
              <div className="text-lg font-bold text-rose-600 dark:text-rose-400" data-testid="cn-total">Credit total AED {money(totals.total)}</div>
            </div>
          </div>
          <div className="flex items-center justify-end gap-3">
            {(!editing.reason?.trim() || totals.total <= 0) && (
              <span className="text-xs text-amber-600 dark:text-amber-400">Add a reason and at least one credit amount</span>
            )}
            <Button className="gap-1.5" data-testid="cn-save" disabled={busy || !editing.reason?.trim() || totals.total <= 0} onClick={save}>{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} {editing.id ? "Save changes" : "Create credit note"}</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="credit-notes-page">
      <button onClick={() => navigate("/einvoicing/app")} data-testid="cn-back" className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground mb-4"><ArrowLeft className="w-4 h-4" /> Matiqs Qonnect</button>
      <div className="flex items-start justify-between gap-3 mb-6 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white flex items-center gap-2"><Undo2 className="w-6 h-6 text-rose-500" /> Credit notes</h1>
          <p className="text-sm text-muted-foreground mt-1">Correct a cleared invoice compliantly with a credit note instead of editing the issued document.</p>
        </div>
      </div>

      <div className="rounded-xl border border-dashed border-border bg-card p-4 mb-6 text-sm text-muted-foreground">
        Start a credit note from any invoice: open <strong>Standard invoices</strong> or a <strong>construction IPC</strong> and use its <em>Credit note</em> action. It arrives here prefilled and editable.
      </div>

      {list.length === 0 ? (
        <div data-testid="cn-empty" className="rounded-2xl border border-dashed border-border bg-card py-16 text-center">
          <Undo2 className="w-10 h-10 text-muted-foreground/50 mx-auto mb-3" />
          <p className="text-muted-foreground">No credit notes yet.</p>
        </div>
      ) : (
        <div className="rounded-2xl border border-border bg-card overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-accent text-[10px] uppercase tracking-wider text-muted-foreground">
              <tr>
                <th className="px-4 py-2.5 text-left font-bold">Credit note</th>
                <th className="px-4 py-2.5 text-left font-bold">Against</th>
                <th className="px-4 py-2.5 text-left font-bold">Customer</th>
                <th className="px-4 py-2.5 text-right font-bold">Total (AED)</th>
                <th className="px-4 py-2.5 text-left font-bold">Approved ASP</th>
                <th className="px-4 py-2.5 text-right font-bold">Export</th>
                <th className="px-4 py-2.5 text-right font-bold"></th>
              </tr>
            </thead>
            <tbody>
              {list.map((cn) => {
                const sub = subs[cn.id];
                return (
                  <tr key={cn.id} className="border-t border-border" data-testid={`cn-row-${cn.cn_number}`}>
                    <td className="px-4 py-3 font-semibold text-foreground">{cn.cn_number}</td>
                    <td className="px-4 py-3 text-muted-foreground">{cn.source_invoice_number || "—"}</td>
                    <td className="px-4 py-3">{cn.customer_name || "—"}</td>
                    <td className="px-4 py-3 text-right font-semibold text-rose-600 dark:text-rose-400">{money(cn.total)}</td>
                    <td className="px-4 py-3">
                      {sub && sub.status !== "ERROR" ? (
                        <div className="flex flex-col items-start gap-1">
                          <span data-testid={`cn-jam-status-${cn.cn_number}`} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold ${STATUS_STYLE[sub.status] || "bg-accent text-muted-foreground"}`}>
                            {sub.status === "RECEIVED" && <Loader2 className="w-3 h-3 animate-spin" />}{DONE_OK.includes(sub.status) && <CheckCircle2 className="w-3 h-3" />}{DONE_BAD.includes(sub.status) && <AlertTriangle className="w-3 h-3" />}{sub.status_label || sub.status}
                          </span>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-[9px] font-bold uppercase tracking-wide px-1.5 py-0.5 rounded ${sub.mode === "live" ? "bg-indigo-100 text-indigo-700 dark:bg-indigo-950/40 dark:text-indigo-300" : "bg-accent text-muted-foreground"}`}>{sub.mode_label || (sub.mode === "live" ? "Live · Approved ASP" : "Simulation")}</span>
                            <button type="button" data-testid={`cn-jam-clearance-${cn.cn_number}`} onClick={() => navigate("/einvoicing/clearance")} className="text-[10px] font-semibold text-indigo-600 dark:text-indigo-400 hover:underline">Clearance →</button>
                          </div>
                        </div>
                      ) : (
                        <Button data-testid={`cn-jam-submit-${cn.cn_number}`} variant="outline" size="sm" className="gap-1 h-7 text-xs border-indigo-300 text-indigo-700 dark:text-indigo-300" disabled={subBusy === cn.id} onClick={() => submitJam(cn)}>
                          {subBusy === cn.id ? <Loader2 className="w-3 h-3 animate-spin" /> : <Send className="w-3 h-3" />} {sub && sub.status === "ERROR" ? "Retry" : "Submit to FTA"}
                        </Button>
                      )}
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1">
                        {["pdf", "xlsx", "xml"].map((f) => (
                          <Button key={f} variant="ghost" size="sm" className="h-7 px-2 text-xs uppercase" data-testid={`cn-dl-${f}-${cn.cn_number}`} disabled={dl === cn.id + f} onClick={() => download(cn, f)}>
                            {dl === cn.id + f ? <Loader2 className="w-3 h-3 animate-spin" /> : f}
                          </Button>
                        ))}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1">
                        {sub && sub.status !== "ERROR" ? (
                          <span className="text-[10px] text-muted-foreground italic" data-testid={`cn-locked-${cn.cn_number}`}>Submitted — locked</span>
                        ) : (
                          <>
                            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs" data-testid={`cn-edit-${cn.cn_number}`} onClick={() => openEdit(cn)}><Pencil className="w-3.5 h-3.5" /></Button>
                            <Button variant="ghost" size="sm" className="h-7 gap-1 text-xs text-muted-foreground hover:text-destructive" data-testid={`cn-delete-${cn.cn_number}`} onClick={() => remove(cn)}><Trash2 className="w-3.5 h-3.5" /></Button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
